/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { RatingStat } from '@coursera/cds-core';

import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';

type Props = { avgProductRating?: number; numProductRatings?: number };

const ProductCardReviews = ({ avgProductRating, numProductRatings }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // WCAG 4.1.2 fix: CDS RatingStat renders role="meter" which is not well-supported
  // by screen readers. Patch it to role="img" with an aria-label so the rating
  // is announced as a single readable value (e.g. "4.7 out of 5 stars").
  useEffect(() => {
    const meterEl = containerRef.current?.querySelector('[role="meter"]');
    if (meterEl) {
      meterEl.setAttribute('role', 'img');
      meterEl.setAttribute('aria-roledescription', 'rating');
      const valueText = meterEl.getAttribute('aria-valuetext');
      if (valueText) {
        meterEl.setAttribute('aria-label', valueText);
      }
      meterEl.removeAttribute('aria-valuemin');
      meterEl.removeAttribute('aria-valuemax');
      meterEl.removeAttribute('aria-valuenow');
      meterEl.removeAttribute('aria-valuetext');
    }
  }, [avgProductRating, numProductRatings]);

  if (!avgProductRating || (numProductRatings && numProductRatings <= 5)) {
    return null;
  }

  const rating = Math.round(avgProductRating * 10) / 10;
  const experimentVariant = SiteMiscExperiments.preview('productCardTest1');
  const size = experimentVariant === 'test1' ? 'meta' : 'label';

  return (
    <div ref={containerRef}>
      <RatingStat rating={rating} size={size} reviews={numProductRatings} />
    </div>
  );
};

export default ProductCardReviews;
